'use client';
import React, { useState } from 'react';
import { Plus, RefreshCw, Edit, Check, X, Save } from 'lucide-react';

export default function CurrenciesManagers() {
  const [currencies, setCurrencies] = useState([
    { name: 'US Dollar', code: 'USD', rate: 1, order: 0, published: true },
    { name: 'Australian Dollar', code: 'AUD', rate: 1.34, order: 2, published: true },
    { name: 'British Pound', code: 'GBP', rate: 0.75, order: 3, published: true },
    { name: 'Canadian Dollar', code: 'CAD', rate: 1.32, order: 4, published: true },
    { name: 'Chinese Yuan Renminbi', code: 'CNY', rate: 6.43, order: 5, published: true },
    { name: 'Euro', code: 'EUR', rate: 0.86, order: 6, published: true },
    { name: 'Hong Kong Dollar', code: 'HKD', rate: 7.84, order: 7, published: true },
    { name: 'Japanese Yen', code: 'JPY', rate: 110.45, order: 8, published: true },
    { name: 'Russian Rouble', code: 'RUB', rate: 63.25, order: 9, published: true },
    { name: 'Swedish Krona', code: 'SEK', rate: 8.8, order: 10, published: true },
    { name: 'Indian Rupee', code: 'INR', rate: 68.03, order: 12, published: true },
  ]);

  const [showModal, setShowModal] = useState(false);
  const [newCurrency, setNewCurrency] = useState({
    name: '',
    code: '',
    rate: '',
    order: '',
    published: true,
  });

  const handleAddCurrency = () => {
    if (!newCurrency.name || !newCurrency.code) return;
    setCurrencies([...currencies, newCurrency]);
    setShowModal(false);
    setNewCurrency({ name: '', code: '', rate: '', order: '', published: true });
  };

  const handleLiveRates = () => {
    // Simulate live rate update
    const updated = currencies.map(c => ({
      ...c,
      rate: (c.rate * (0.95 + Math.random() * 0.1)).toFixed(2),
    }));
    setCurrencies(updated);
    alert('✅ Live currency rates updated!');
  };

  return (
    <div className="p-4 sm:p-8 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto bg-white shadow-md rounded-lg p-6">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
          <h2 className="text-2xl font-semibold text-gray-800">Currencies</h2>

          <div className="flex gap-2 flex-wrap">
            <button
              onClick={handleLiveRates}
              className="bg-green-600 text-white px-3 py-2 rounded flex items-center hover:bg-green-700"
            >
              <RefreshCw className="w-4 h-4 mr-1" /> Live Rate
            </button>

            <button
              onClick={() => setShowModal(true)}
              className="bg-blue-600 text-white px-3 py-2 rounded flex items-center hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-1" /> Add New
            </button>

            <button
              className="bg-gray-800 text-white px-3 py-2 rounded flex items-center hover:bg-gray-900"
            >
              <Save className="w-4 h-4 mr-1" /> Save
            </button>
          </div>
        </div>

        {/* Info */}
        <div className="mb-4 text-gray-700 space-y-1">
          <p>
            <strong>Current exchange rate provider:</strong> Open Exchange Rates
          </p>
          <p>
            <strong>Auto update enabled:</strong> Yes
          </p>
          <p className="text-sm text-gray-600">
            Learn more about{' '}
            <a href="#" className="text-blue-600 underline">
              currencies
            </a>
          </p>
        </div>

        {/* Table */}
        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full border-collapse text-sm text-gray-700">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-3 border-b text-left">Name</th>
                <th className="p-3 border-b text-left">Currency code</th>
                <th className="p-3 border-b text-left">Rate</th>
                <th className="p-3 border-b text-center">Is primary exchange rate currency</th>
                <th className="p-3 border-b text-center">Mark as primary exchange rate currency</th>
                <th className="p-3 border-b text-center">Is primary store currency</th>
                <th className="p-3 border-b text-center">Mark as primary store currency</th>
                <th className="p-3 border-b text-center">Display order</th>
                <th className="p-3 border-b text-center">Published</th>
                <th className="p-3 border-b text-center">Edit</th>
              </tr>
            </thead>
            <tbody>
              {currencies.map((cur, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="p-3 border-b">{cur.name}</td>
                  <td className="p-3 border-b">{cur.code}</td>
                  <td className="p-3 border-b">{cur.rate}</td>
                  <td className="p-3 border-b text-center">
                    {cur.code === 'USD' ? <Check className="text-blue-600 w-4 h-4 inline" /> : '—'}
                  </td>
                  <td className="p-3 border-b text-center">
                    <button className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs">
                      Mark
                    </button>
                  </td>
                  <td className="p-3 border-b text-center">—</td>
                  <td className="p-3 border-b text-center">
                    <button className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs">
                      Mark
                    </button>
                  </td>
                  <td className="p-3 border-b text-center">{cur.order}</td>
                  <td className="p-3 border-b text-center">
                    {cur.published ? (
                      <Check className="text-green-600 w-4 h-4 inline" />
                    ) : (
                      <X className="text-red-500 w-4 h-4 inline" />
                    )}
                  </td>
                  <td className="p-3 border-b text-center">
                    <button className="bg-gray-100 px-3 py-1 rounded hover:bg-gray-200 flex items-center mx-auto">
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-wrap justify-between items-center mt-4 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <button className="px-3 py-1 border rounded hover:bg-gray-100">Previous</button>
            <button className="px-3 py-1 border rounded bg-blue-50 text-blue-700">1</button>
            <button className="px-3 py-1 border rounded hover:bg-gray-100">Next</button>
          </div>

          <div className="flex items-center gap-2">
            <label>Show</label>
            <select className="border rounded px-2 py-1">
              <option>15</option>
              <option>30</option>
              <option>50</option>
            </select>
            <span>items</span>
          </div>
          <span className="text-gray-500">1–{currencies.length} of {currencies.length} items</span>
        </div>
      </div>

      {/* Add New Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-96">
            <h3 className="text-xl font-semibold mb-4">Add New Currency</h3>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  value={newCurrency.name}
                  onChange={(e) => setNewCurrency({ ...newCurrency, name: e.target.value })}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Currency code</label>
                <input
                  type="text"
                  value={newCurrency.code}
                  onChange={(e) => setNewCurrency({ ...newCurrency, code: e.target.value })}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Rate</label>
                <input
                  type="number"
                  step="0.01"
                  value={newCurrency.rate}
                  onChange={(e) => setNewCurrency({ ...newCurrency, rate: e.target.value })}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Display order</label>
                <input
                  type="number"
                  value={newCurrency.order}
                  onChange={(e) => setNewCurrency({ ...newCurrency, order: e.target.value })}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={newCurrency.published}
                  onChange={(e) => setNewCurrency({ ...newCurrency, published: e.target.checked })}
                  className="mr-2"
                />
                <label>Published</label>
              </div>
            </div>

            <div className="flex justify-end mt-4 gap-2">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCurrency}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
